import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Eye, Printer, FileDown, FolderOpen } from "lucide-react";
import api, { errMsg } from "../lib/api";
import PageHeader from "../components/PageHeader";
import OASearchSelect from "../components/OASearchSelect";
import { useAuth } from "../context/AuthContext";

const STATUSES = [["pending", "Pending"], ["in_progress", "In Progress"], ["hold", "Hold"], ["completed", "Completed"]];
const STATUS_LABEL = Object.fromEntries(STATUSES);
const STATUS_CLS = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  hold: "bg-slate-100 text-slate-600 border-slate-200",
  completed: "bg-green-50 text-green-700 border-green-200",
};
const emptyForm = { oaId: "", oaNumber: "", oaDate: "", poNumber: "", customerName: "", customerAddress: "", destination: "", specialInstruction: "", remarks: "", status: "pending", items: [] };

export default function WorkOrders() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const can = (p) => isAdmin || (user.permissions || []).includes(p);
  const [rows, setRows] = useState([]);
  const [sp, setSp] = useSearchParams();
  const [filters, setFilters] = useState({ search: "", oaNumber: "", woNumber: "", customer: "", fromDate: "", toDate: "" });
  const [modal, setModal] = useState(null); // "new" | row being edited
  const [form, setForm] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const load = useCallback(async () => {
    if (!can("view_workorder")) return;
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await api.get("/work-orders", { params });
    setRows(data);
  }, [filters]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const prefillFor = async (oaId) => {
    try {
      const { data } = await api.get(`/oa/${oaId}/work-order-prefill`);
      setForm((p) => (p ? { ...p, oaId: data.oaId, oaNumber: data.oaNumber, oaDate: data.oaDate || "",
        poNumber: data.poNumber || "",
        customerName: data.customerName || "", customerAddress: data.customerAddress || "",
        destination: data.destination || "", specialInstruction: data.specialInstruction || "",
        items: (data.items || []).map((it) => ({ ...it, selected: true })) } : p));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openCreate = (oaId) => { setForm({ ...emptyForm, items: [] }); setModal("new"); if (oaId) prefillFor(oaId); };
  const openEdit = (r) => {
    setForm({ oaId: r.oaId, oaNumber: r.oaNumber, oaDate: r.oaDate || "", poNumber: r.poNumber || "", customerName: r.customerName || "",
      customerAddress: r.customerAddress || "", destination: r.destination || "",
      specialInstruction: r.specialInstruction || "", remarks: r.remarks || "", status: r.status || "pending",
      items: (r.items || []).map((it) => ({ ...it, selected: true })) });
    setModal(r);
  };

  useEffect(() => {
    const cid = sp.get("create");
    if (cid && can("add_workorder")) { setSp({}, { replace: true }); openCreate(cid); }
  }, []); // eslint-disable-line

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setItem = (i, k, v) => setForm((p) => { const arr = [...p.items]; arr[i] = { ...arr[i], [k]: v }; return { ...p, items: arr }; });

  const save = async () => {
    if (!form.oaId) return toast.error("Select an OA Number first — a Work Order must be linked to an OA.");
    const items = form.items.filter((i) => i.selected).map(({ selected, ...rest }) => rest);
    if (modal === "new" && items.length === 0) return toast.error("Select at least one product for the Work Order.");
    try {
      const payload = { oaId: form.oaId, oaDate: form.oaDate, poNumber: form.poNumber, customerName: form.customerName,
        customerAddress: form.customerAddress, destination: form.destination, items,
        status: form.status, remarks: form.remarks, specialInstruction: form.specialInstruction };
      if (modal === "new") {
        const { data } = await api.post("/work-orders", payload);
        toast.success(`Work Order ${data.woNumber} created`);
      } else {
        await api.put(`/work-orders/${modal.id}`, payload);
        toast.success("Work Order updated");
      }
      setModal(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete Work Order ${r.woNumber}?`)) return;
    try { await api.delete(`/work-orders/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const printWO = (r) => {
    api.get(`/work-orders/${r.id}/html`).then(({ data }) => {
      const w = window.open("", "_blank"); if (!w) return;
      w.document.write(data); w.document.close(); w.onload = () => w.print();
    }).catch((e) => toast.error(errMsg(e)));
  };
  const downloadPdf = (r) => {
    toast.info("Generating Work Order PDF…");
    api.get(`/work-orders/${r.id}/pdf`, { responseType: "blob" })
      .then(({ data }) => { const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = `work-order-${r.woNumber}.pdf`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 60000); })
      .catch((e) => toast.error(errMsg(e)));
  };

  if (!can("view_workorder")) return <div className="p-8 text-slate-500">You do not have permission to view Work Orders.</div>;

  const itemSummary = (r) => {
    const names = (r.items || []).map((i) => i.item).filter(Boolean);
    if (!names.length) return "—";
    return names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2} more` : "");
  };

  return (
    <div data-testid="workorders-page">
      <PageHeader title="Work Orders" subtitle={`${rows.length} record(s)`} actions={
        can("add_workorder") && <button className="btn-accent" onClick={() => openCreate()} data-testid="wo-add-btn"><Plus className="h-4 w-4" /> Create Work Order</button>
      } />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="card p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <input className="input" placeholder="Search" value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} data-testid="wo-filter-search" />
          <OASearchSelect value={filters.oaNumber} onText={(v) => setFilters((p) => ({ ...p, oaNumber: v }))} onPick={(o) => setFilters((p) => ({ ...p, oaNumber: o.oaNumber }))} placeholder="OA Number" testid="wo-filter-oa" />
          <input className="input" placeholder="WO Number" value={filters.woNumber} onChange={(e) => setFilters((p) => ({ ...p, woNumber: e.target.value }))} data-testid="wo-filter-wo" />
          <input className="input" placeholder="Customer" value={filters.customer} onChange={(e) => setFilters((p) => ({ ...p, customer: e.target.value }))} data-testid="wo-filter-customer" />
          <input type="date" className="input" value={filters.fromDate} onChange={(e) => setFilters((p) => ({ ...p, fromDate: e.target.value }))} data-testid="wo-filter-from" />
          <input type="date" className="input" value={filters.toDate} onChange={(e) => setFilters((p) => ({ ...p, toDate: e.target.value }))} data-testid="wo-filter-to" />
        </div>

        <div className="card overflow-hidden"><div className="overflow-x-auto"><table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            <th className="th">WO No.</th><th className="th">OA No.</th><th className="th">Customer</th><th className="th">Destination</th>
            <th className="th">Products</th><th className="th">Status</th><th className="th">Created</th><th className="th">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td className="td text-slate-400" colSpan={8}><div className="flex items-center gap-2 py-6 justify-center" data-testid="wo-empty"><FolderOpen className="h-5 w-5" /> No work orders yet.</div></td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`wo-row-${r.id}`}>
                <td className="td font-mono font-semibold">{r.woNumber}</td>
                <td className="td font-mono">{r.oaNumber}</td>
                <td className="td">{r.customerName || "—"}</td>
                <td className="td">{r.destination || "—"}</td>
                <td className="td text-xs">{itemSummary(r)}</td>
                <td className="td"><span className={`badge border ${STATUS_CLS[r.status] || "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid={`wo-status-${r.id}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                <td className="td text-xs text-slate-500">{(r.createdAt || "").slice(0, 10)}<br />{r.createdByName}</td>
                <td className="td"><div className="flex items-center gap-1">
                  <button title="View" className="p-1.5 rounded hover:bg-slate-100" onClick={() => setViewRow(r)} data-testid={`wo-view-${r.id}`}><Eye className="h-4 w-4 text-slate-600" /></button>
                  {can("edit_workorder") && <button title="Edit" className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(r)} data-testid={`wo-edit-${r.id}`}><Pencil className="h-4 w-4 text-slate-600" /></button>}
                  <button title="Print" className="p-1.5 rounded hover:bg-slate-100" onClick={() => printWO(r)} data-testid={`wo-print-${r.id}`}><Printer className="h-4 w-4 text-slate-600" /></button>
                  <button title="Download PDF" className="p-1.5 rounded hover:bg-slate-100" onClick={() => downloadPdf(r)} data-testid={`wo-pdf-${r.id}`}><FileDown className="h-4 w-4 text-slate-600" /></button>
                  {can("delete_workorder") && <button title="Delete" className="p-1.5 rounded hover:bg-red-50" onClick={() => del(r)} data-testid={`wo-delete-${r.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table></div></div>
      </div>

      {modal && form && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="card w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="wo-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-xl font-bold">{modal === "new" ? "Create Work Order" : `Edit ${modal.woNumber}`}</h3>
              <button onClick={() => setModal(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">OA Number *</label>
                {modal === "new" ? (
                  <OASearchSelect value={form.oaNumber} onText={(v) => set("oaNumber", v)} onPick={(o) => prefillFor(o.id)} placeholder="Search / Select O.A. Number" testid="wo-oa-select" />
                ) : (
                  <input className="input bg-slate-50" value={form.oaNumber} disabled data-testid="wo-oa-locked" />
                )}
              </div>
              <div><label className="label">OA Date</label><input type="date" className="input" value={form.oaDate} onChange={(e) => set("oaDate", e.target.value)} data-testid="wo-field-oaDate" /></div>
              <div><label className="label">P.O. No.</label><input className="input" value={form.poNumber} onChange={(e) => set("poNumber", e.target.value)} data-testid="wo-field-poNumber" /></div>
              <div><label className="label">Customer / Consignee</label><input className="input" value={form.customerName} onChange={(e) => set("customerName", e.target.value)} data-testid="wo-field-customerName" /></div>
              <div><label className="label">Destination</label><input className="input" value={form.destination} onChange={(e) => set("destination", e.target.value)} data-testid="wo-field-destination" /></div>
              <div className="md:col-span-2"><label className="label">Customer Address</label><input className="input" value={form.customerAddress} onChange={(e) => set("customerAddress", e.target.value)} data-testid="wo-field-customerAddress" /></div>
              <div><label className="label">Status</label>
                <select className="input" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="wo-field-status">
                  {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div><label className="label">Remarks</label><input className="input" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} data-testid="wo-field-remarks" /></div>
              <div className="md:col-span-2"><label className="label">Special Information / Instruction</label><input className="input" value={form.specialInstruction} onChange={(e) => set("specialInstruction", e.target.value)} data-testid="wo-field-specialInstruction" /></div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="label mb-0">Products from OA ({form.items.filter((i) => i.selected).length}/{form.items.length} selected)</label>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={form.items.length > 0 && form.items.every((i) => i.selected)}
                    onChange={(e) => setForm((p) => ({ ...p, items: p.items.map((i) => ({ ...i, selected: e.target.checked })) }))} data-testid="wo-items-select-all" />
                  Select All
                </label>
              </div>
              {form.oaId && form.items.length === 0 && <p className="text-xs text-slate-400" data-testid="wo-no-products">No products found on this OA.</p>}
              {!form.oaId && <p className="text-xs text-slate-400">Select an OA Number to load its products.</p>}
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className={`border rounded-lg p-3 ${it.selected ? "border-amber-300 bg-amber-50/40" : "border-slate-200 opacity-60"}`} data-testid={`wo-item-${i}`}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={it.selected} onChange={(e) => setItem(i, "selected", e.target.checked)} data-testid={`wo-item-check-${i}`} />
                      <span className="font-medium text-sm text-slate-800">{it.item || "—"}</span>
                    </div>
                    {it.selected && (
                      <div className="grid md:grid-cols-3 gap-2 mt-2">
                        <div><label className="label">Description</label><input className="input" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`wo-item-desc-${i}`} /></div>
                        <div><label className="label">Model / Class</label><input className="input" value={it.modelClass || ""} onChange={(e) => setItem(i, "modelClass", e.target.value)} data-testid={`wo-item-model-${i}`} /></div>
                        <div><label className="label">Size</label><input className="input" value={it.size || ""} onChange={(e) => setItem(i, "size", e.target.value)} data-testid={`wo-item-size-${i}`} /></div>
                        <div><label className="label">Specification</label><input className="input" value={it.specification} onChange={(e) => setItem(i, "specification", e.target.value)} data-testid={`wo-item-spec-${i}`} /></div>
                        <div><label className="label">Qty</label><input type="number" className="input" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`wo-item-qty-${i}`} /></div>
                        <div><label className="label">Remarks</label><input className="input" value={it.remarks} onChange={(e) => setItem(i, "remarks", e.target.value)} data-testid={`wo-item-remarks-${i}`} /></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button className="btn-outline" onClick={() => setModal(null)} data-testid="wo-cancel">Cancel</button>
              <button className="btn-primary" onClick={save} data-testid="wo-save">{modal === "new" ? "Create Work Order" : "Save Changes"}</button>
            </div>
          </div>
        </div>
      )}

      {viewRow && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setViewRow(null)}>
          <div className="card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="wo-view-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-xl font-bold flex items-center gap-3">Work Order <span className="font-mono">{viewRow.woNumber}</span>
                <span className={`badge border ${STATUS_CLS[viewRow.status] || ""}`}>{STATUS_LABEL[viewRow.status] || viewRow.status}</span></h3>
              <button onClick={() => setViewRow(null)} data-testid="wo-view-close"><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3 text-sm mb-4">
              <div><span className="text-slate-400">Source OA</span><div className="font-mono font-semibold">{viewRow.oaNumber}</div></div>
              <div><span className="text-slate-400">OA Date</span><div className="font-semibold">{viewRow.oaDate || "—"}</div></div>
              <div><span className="text-slate-400">P.O. No.</span><div className="font-semibold">{viewRow.poNumber || "—"}</div></div>
              <div><span className="text-slate-400">Customer</span><div className="font-semibold">{viewRow.customerName || "—"}</div></div>
              <div><span className="text-slate-400">Destination</span><div className="font-semibold">{viewRow.destination || "—"}</div></div>
              <div className="md:col-span-2"><span className="text-slate-400">Customer Address</span><div className="font-semibold">{viewRow.customerAddress || "—"}</div></div>
              <div><span className="text-slate-400">Created By</span><div className="font-semibold">{viewRow.createdByName || "—"}</div></div>
              <div><span className="text-slate-400">Created At</span><div className="font-semibold">{(viewRow.createdAt || "").slice(0, 16).replace("T", " ")}</div></div>
            </div>
            <table className="w-full border-collapse text-sm mb-4">
              <thead><tr className="bg-slate-50">{["Sr", "Item", "Description", "Model / Class", "Size", "Specification", "Qty", "Remarks"].map((h) => <th key={h} className="border border-slate-200 px-2 py-1.5 text-left">{h}</th>)}</tr></thead>
              <tbody>
                {(viewRow.items || []).map((it, i) => (
                  <tr key={i}>{[i + 1, it.item, it.description, it.modelClass, it.size, it.specification, it.qty, it.remarks].map((v, j) => <td key={j} className="border border-slate-200 px-2 py-1.5">{v || "—"}</td>)}</tr>
                ))}
                {!(viewRow.items || []).length && <tr><td colSpan={8} className="border border-slate-200 px-2 py-3 text-center text-slate-400">No products.</td></tr>}
              </tbody>
            </table>
            {viewRow.specialInstruction && <p className="text-sm mb-1"><span className="text-slate-400">Special Instruction:</span> {viewRow.specialInstruction}</p>}
            {viewRow.remarks && <p className="text-sm mb-1"><span className="text-slate-400">Remarks:</span> {viewRow.remarks}</p>}
            <div className="flex justify-end gap-2 mt-5">
              <button className="btn-outline btn-sm" onClick={() => printWO(viewRow)} data-testid="wo-view-print"><Printer className="h-4 w-4" /> Print</button>
              <button className="btn-accent btn-sm" onClick={() => downloadPdf(viewRow)} data-testid="wo-view-pdf"><FileDown className="h-4 w-4" /> Download PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
